import { PERMISSIONS } from 'shared-permissions';
const {
  SHOWS,
  SHOW_STATS,
  MANAGE_OWN_SHOWS,
  MODERATE,
  LANGUAGE,
  INTEGRATIONS_ONE_TO_MANY,
  EDIT_USERS_ONE_TO_MANY,
  DATA_EXPORT_SERVICE_EXPORT_JOBS,
  MANAGE_AI_GROUNDINGS,
  MANAGE_AI_VISIBILITY,
  WORKSPACE_SETTINGS,
  LOCALIZATION_ONE_TO_MANY,
} = PERMISSIONS;
import {
  ORG_INTEGRATIONS,
  ORG_MODULES,
  ORG_FLOATING_WIDGET,
  ORG_SHOWS,
  ORG_STATS,
  ORG_THEMES,
  ORG_LOCALIZATION,
  ORG_USERS,
  ORG_DATA_REPORTS,
  ORG_ONE_TO_MANY_HOME,
  ORG_ONE_TO_MANY_THEMES,
  ORG_EMBED_STATS,
  ORG_ONE_TO_MANY_AI,
  ORG_ONE_TO_MANY_AIV,
  ORG_GENERAL_SETTINGS,
  ORG_DATA_EXPORT_JOBS,
  ORG_PRODUCTS,
  ORG_PRODUCTS_INTELLIGENCE,
  ORG_PRODUCTS_AI_VISIBILITY,
  ORG_CATALOG_PRODUCT_VIEW,
  ORG_CATALOG_PRODUCT_KNOWLEDGE,
  ORG_CATALOG_PRODUCT_AIV,
} from 'constants/routes.js';
import { playerV2Filter } from './utils.js';

const PRODUCT_CATALOG_PERMISSIONS = [
  MANAGE_OWN_SHOWS,
  SHOWS,
  MODERATE,
  INTEGRATIONS_ONE_TO_MANY,
];
const PRODUCT_INTELLIGENCE_PERMISSIONS = [
  ...PRODUCT_CATALOG_PERMISSIONS,
  MANAGE_AI_VISIBILITY,
];

const ONE_TO_MANY_ROUTES = [
  {
    title: 'sidebarComponent.homeTitle',
    icon: 'home',
    route: ORG_ONE_TO_MANY_HOME,
    dataTestId: 'homeMenuItem',
    requiredFeatures: [],
    requiredPermission: [MANAGE_OWN_SHOWS, SHOWS, MODERATE],
    allowSuperAdmin: true,
    exact: true,
    shownInMasquerade: false,
  },
  {
    title: 'sidebarComponent.showsTitle',
    icon: 'shows',
    route: ORG_SHOWS,
    dataTestId: 'showsMenuItem',
    requiredFeatures: ['showsEnabled'],
    requiredPermission: [MANAGE_OWN_SHOWS, SHOWS, MODERATE],
    allowSuperAdmin: true,
  },
  {
    title: 'sidebarComponent.productsTitle',
    icon: 'bag',
    submenu: [
      {
        title: 'aivPage.productsCatalogTab',
        defaultTitle: 'Catalog',
        route: ORG_PRODUCTS,
        // Without `exact` this route matches the Intelligence and AI
        // Visibility listings and every /products/:id/... detail page too.
        exact: true,
        // The product's own General tab is the catalog's page, so it keeps the
        // highlight. Exact, or it would swallow the knowledge and AI
        // Visibility tabs nested under the same /view path.
        activePaths: [{ path: ORG_CATALOG_PRODUCT_VIEW, exact: true }],
        requiredPermission: PRODUCT_CATALOG_PERMISSIONS,
        allowSuperAdmin: true,
      },
      {
        title: 'pagesCatalogProducts.intelligenceTabName',
        defaultTitle: 'Intelligence',
        route: ORG_PRODUCTS_INTELLIGENCE,
        // The Intelligence product tab, and everything nested under it -- a
        // category drill-in and a single atom are still that tab, so this stays
        // inexact.
        activePaths: [{ path: ORG_CATALOG_PRODUCT_KNOWLEDGE }],
        requiredFeatures: ['productKnowledgeLayerEnabled'],
        requiredPermission: PRODUCT_INTELLIGENCE_PERMISSIONS,
        allowSuperAdmin: true,
      },
      {
        title: 'aivPage.productTabTitle',
        defaultTitle: 'AI Visibility',
        route: ORG_PRODUCTS_AI_VISIBILITY,
        activePaths: [{ path: ORG_CATALOG_PRODUCT_AIV }],
        requiredFeatures: ['aiVisibilityEnabled'],
        requiredPermission: [MANAGE_AI_VISIBILITY],
        allowSuperAdmin: true,
      },
    ],
    // Either layer is enough to want the submenu: with only AI Visibility on,
    // its entry belongs here rather than being stranded behind a gate for a
    // feature the org does not have. Each entry still carries its own flag, so
    // the submenu holds Catalog plus whichever of the two is on.
    requiredFeatures: ['productCatalogProductsLiveEnabled', ['productKnowledgeLayerEnabled', 'aiVisibilityEnabled']],
    requiredPermission: PRODUCT_INTELLIGENCE_PERMISSIONS,
    dataTestId: 'productsMenuItem',
    allowSuperAdmin: true,
  },
  {
    title: 'sidebarComponent.productsTitle',
    icon: 'bag',
    route: ORG_PRODUCTS,
    // The flat catalog entry, for an org with neither layer. Both negations, or
    // it would render beside the submenu above whenever only one is on.
    requiredFeatures: ['productCatalogProductsLiveEnabled', '!productKnowledgeLayerEnabled', '!aiVisibilityEnabled'],
    requiredPermission: PRODUCT_CATALOG_PERMISSIONS,
    dataTestId: 'productsMenuItem',
    allowSuperAdmin: true,
  },
  {
    title: 'sidebarComponent.statsTitle',
    icon: 'insights',
    submenu: [
      {
        title: 'sidebarComponent.statsEmbedTitle',
        route: ORG_EMBED_STATS,
        requiredFeatures: ['showsEnabled', { name: 'showStatsEmbed', allowUpsell: true }],
        requiredPermission: [SHOW_STATS],
        allowSuperAdmin: true,
        exact: true,
        flair: {
          id: 'sidebarFlair-sub-menu-insights',
        },
        upsellFeatureName: 'showStatsEmbed',
      },
      {
        title: 'showStatsPageTabs.tabTitleShowsList',
        route: ORG_STATS,
        routeParams: { pageName: 'showsList' },
        requiredFeatures: ['showsEnabled'],
        allowSuperAdmin: true,
        requiredPermission: [SHOW_STATS, MANAGE_OWN_SHOWS],
        flair: {
          id: 'sidebarFlair-sub-menu-showsList',
        },
      },
      {
        title: 'showStatsPageTabs.tabTitleSummary',
        route: ORG_STATS,
        routeParams: { pageName: 'summary' },
        requiredFeatures: ['showsEnabled', { name: 'showStatsEmbeddableInsights', state: 'disabled' }],
        allowSuperAdmin: true,
        requiredPermission: [SHOW_STATS],
        shownInMasquerade: false,
      },
      {
        title: 'showStatsPageTabs.tabTitleDataDispatch',
        route: ORG_DATA_REPORTS,
        requiredFeatures: ['showsEnabled', 'showStatsDataReportsEnabled'],
        allowSuperAdmin: true,
        requiredPermission: [DATA_EXPORT_SERVICE_EXPORT_JOBS],
      },
      {
        title: 'sidebarComponent.dataExportJobsTitle',
        route: ORG_DATA_EXPORT_JOBS,
        requiredFeatures: ['showsEnabled', { name: 'dataExport', allowUpsell: true }],
        allowSuperAdmin: true,
        requiredPermission: [SHOW_STATS],
        upsellFeatureName: 'dataExport',
      },
    ],
  },
  {
    title: 'sidebarComponent.widgetTitle',
    icon: 'widgets',
    submenu: [
      {
        title: 'sidebarComponent.channelsTitle',
        route: ORG_MODULES,
        requiredFeatures: ['showsEnabled', 'modulesEnabled'],
        requiredPermission: SHOWS,
        allowSuperAdmin: true,
      },
      {
        title: 'sidebarComponent.floatingActionsTitle',
        route: ORG_FLOATING_WIDGET,
        requiredFeatures: ['showsEnabled', 'widgetsEnabled'],
        requiredPermission: SHOWS,
        allowSuperAdmin: true,
      },
    ],
  },
  {
    // AI Visibility remains independent of product Intelligence.
    title: 'sidebarComponent.aiVisibilityTitle',
    defaultTitle: 'AI Visibility',
    icon: 'curve',
    route: ORG_ONE_TO_MANY_AIV,
    dataTestId: 'aivMenuItem',
    requiredFeatures: ['aiVisibilityEnabled'],
    requiredPermission: [MANAGE_AI_VISIBILITY],
    allowSuperAdmin: true,
  },
  {
    title: 'sidebarComponent.settingsTitle',
    icon: 'settings',
    align: 'bottom',
    submenu: [
      {
        title: 'sidebarComponent.generalSettingsTitle',
        route: ORG_GENERAL_SETTINGS,
        requiredFeatures: [],
        // GEO brand configuration lives inside General Settings. Either
        // permission exposes the entry; the page itself renders only the
        // sections that permission is allowed to manage.
        requiredPermission: [WORKSPACE_SETTINGS, MANAGE_AI_VISIBILITY],
        allowSuperAdmin: true,
      },
      {
        title: 'sidebarComponent.usersSettingsTitle',
        route: ORG_USERS,
        requiredFeatures: [],
        requiredPermission: EDIT_USERS_ONE_TO_MANY,
        allowSuperAdmin: true,
      },
      {
        title: 'sidebarComponent.localizationTitle',
        route: ORG_LOCALIZATION,
        requiredFeatures: ['selfServeTranslationsEnabled'],
        requiredPermission: LOCALIZATION_ONE_TO_MANY,
        allowSuperAdmin: true,
      },
      {
        title: 'sidebarComponent.themesTitle',
        route: ORG_THEMES,
        requiredFeatures: ['themesEnabled', 'showsEnabled'],
        requiredPermission: INTEGRATIONS_ONE_TO_MANY,
        allowSuperAdmin: true,
      },
      {
        title: 'sidebarComponent.appIntegrationsTitle',
        route: ORG_INTEGRATIONS,
        requiredFeatures: ['showsEnabled'],
        requiredPermission: INTEGRATIONS_ONE_TO_MANY,
        allowSuperAdmin: true,
      },
      {
        title: 'sidebarComponent.themingTitle',
        route: ORG_ONE_TO_MANY_THEMES,
        requiredFeatures: ['oneToManyThemingEnabled', 'showsEnabled'],
        requiredPermission: INTEGRATIONS_ONE_TO_MANY,
        customFilterFunction: playerV2Filter,
        allowSuperAdmin: true,
      },
      {
        title: 'sidebarComponent.aiGroundingsModerationTitle',
        route: ORG_ONE_TO_MANY_AI,
        dataTestId: 'AIGroundingsModerationMenuItem',
        requiredFeatures: ['showsEnabled', { name: 'AIGroundingsModeration', allowUpsell: true }],
        requiredPermission: MANAGE_AI_GROUNDINGS,
        allowSuperAdmin: true,
        // `flair` used to be this item's only upsell badge, back when it was
        // a top-level icon (which never reads `upsellFeatureName`). Now that
        // it's a submenu row, SubMenuItem renders `upsellFeatureName` as its
        // own crown, and the `sidebarFlair-ai-groundings` callout resolves to
        // a second one — dropping `flair` leaves the one standard badge every
        // other upsell-eligible submenu row already uses.
        upsellFeatureName: 'AIGroundingsModeration',
      },
    ],
  },
];

export default ONE_TO_MANY_ROUTES;
